import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { StatusBadge, RiskPill, fmtDate, fmtCurrency, fmtRelative } from "../lib/status";
import { toast } from "sonner";
import { Copy, Mail, RefreshCw, Ban, CheckCircle, AlertTriangle, ArrowLeft, Clock, History, AlertOctagon, Wallet, ArrowRightLeft, ClipboardCheck, MapPin, Navigation, KeyRound, Users, Car } from "lucide-react";
import { fmtPHP, recordMeta, statusTone } from "../lib/finance";
import ReassignVehicleModal from "../components/ReassignVehicleModal";
import InspectionSheet from "../components/InspectionSheet";
import DepositPanel from "../components/DepositPanel";
import BookingFinancePanel from "../components/finance/BookingFinancePanel";
import BookingRiskChip from "../components/BookingRiskChip";
import BookingTimeline from "../components/BookingTimeline";
import ConfirmationHistoryPanel from "../components/ConfirmationHistoryPanel";
import DamageClarificationCard from "../components/DamageClarificationCard";
import ReviewInviteCard from "../components/ReviewInviteCard";
import PickupHandoverPanel from "../components/pickup-handover/PickupHandoverPanel";
import PickupHandoverWizard from "../components/pickup-handover/PickupHandoverWizard";
import ReturnReadinessPanel from "../components/ReturnReadinessPanel";
import ResolveDepositPanel from "../components/deposit/ResolveDepositPanel";
import CommunicationsPanel from "../components/communications/CommunicationsPanel";
import BookingChatPanel from "../components/booking-chat/BookingChatPanel";
import DriverAssignmentPanel from "../components/drivers/DriverAssignmentPanel";
import RenterDriverPanel from "../components/renter-drivers/RenterDriverPanel";
import RentalLifecyclePanel from "../components/rental-lifecycle/RentalLifecyclePanel";
import ChangeRequestReviewPanel from "../components/ChangeRequestReviewPanel";
import StaffAssignmentPanel from "../components/StaffAssignmentPanel";
import ExtendRentalModal from "../components/ExtendRentalModal";
import { useAuth } from "../lib/auth";

export default function BookingDetail() {
  const { company } = useAuth();
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reminders, setReminders] = useState([]);
  const [financeRecords, setFinanceRecords] = useState([]);
  const [financeLocked, setFinanceLocked] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/bookings/${id}`);
      setData(data);
      try {
        const r = await api.get(`/reminders`, { params: { booking_id: id } });
        setReminders(r.data);
      } catch (_) { /* reminders are optional */ }
      // Finance records are Pro/Enterprise only and require `finances` perm.
      // We swallow 403s so Basic / non-permitted users just don't see the section.
      try {
        const f = await api.get(`/administration/bookings/${id}/records`);
        setFinanceRecords(f.data.items || []);
        setFinanceLocked(false);
      } catch (e) {
        setFinanceLocked(true);
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  const [reassignOpen, setReassignOpen] = useState(false);
  const [extendOpen, setExtendOpen] = useState(false);
  const [inspectionOpen, setInspectionOpen] = useState(null); // 'return' | null: pickup now only opens via the handover wizard (Phase 2G)
  const [inspections, setInspections] = useState({ pickup: null, return: null });
  const [justReturned, setJustReturned] = useState(false); // Phase 2H: briefly highlights ResolveDepositPanel right after a return inspection finalizes

  const loadInspections = useCallback(async () => {
    try {
      const { data } = await api.get(`/bookings/${id}/inspections`);
      setInspections(data.by_type || { pickup: null, return: null });
    } catch (_) { /* ok if no permission */ }
  }, [id]);
  useEffect(() => {
    let cancelled = false;
    (async () => { if (!cancelled) await loadInspections(); })();
    return () => { cancelled = true; };
  }, [loadInspections]);

  useEffect(() => {
    let cancelled = false;
    (async () => { if (!cancelled) await load(); })();
    return () => { cancelled = true; };
  }, [load]);

  const action = async (path, label) => {
    try {
      await api.post(`/bookings/${id}/${path}`);
      toast.success(`${label} done`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const [handoverWizardOpen, setHandoverWizardOpen] = useState(false);
  const [returnBusy, setReturnBusy] = useState(false);

  const recordReturn = async () => {
    setReturnBusy(true);
    try {
      await api.post(`/bookings/${id}/complete`);
      toast.success("Return recorded: vehicle now needs inspection");
      await Promise.all([load(), loadInspections()]);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setReturnBusy(false);
    }
  };

  const resend = async () => {
    try {
      const { data: res } = await api.put(`/bookings/${id}`, { resend_confirmation: true });
      toast.success("Confirmation link resent");
      if (res.confirmation_link) {
        await navigator.clipboard.writeText(res.confirmation_link).catch(() => {});
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (loading) return <div className="text-slate-500" data-testid="booking-detail-loading">Loading…</div>;
  if (!data) return null;
  const b = data.booking;

  const copyLink = async () => {
    if (!data.confirmation_link) return;
    await navigator.clipboard.writeText(data.confirmation_link);
    toast.success("Confirmation link copied");
  };

  return (
    <div className="space-y-6 max-w-5xl" data-testid="booking-detail-page">
      <button onClick={() => nav(-1)} className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900" data-testid="back-btn">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="font-display text-3xl font-bold">{b.customer_name}</h1>
            <StatusBadge status={b.status} data-testid="detail-status-badge"
                         isHold={b.is_hold} changeRequested={b.change_requested}
                         cancelledBy={b.cancelled_by} cancellationReason={b.cancellation_reason}
                         reconfirmationRequired={b.reconfirmation_required} />
            <RiskPill risk={b.risk_status} />
          </div>
          <p className="font-mono text-sm text-slate-500 mt-1">{b.booking_reference}</p>
          {b.returning_customer?.is_returning && (
            <Link
              to={`/app/customers/${encodeURIComponent(b.customer_email)}`}
              data-testid="detail-returning-customer-pill"
              className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-medium hover:bg-emerald-100"
            >
              <Users className="w-3 h-3" />
              Returning customer · {b.returning_customer.previous_count} previous booking{b.returning_customer.previous_count === 1 ? "" : "s"}
            </Link>
          )}
        </div>
        <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2" data-testid="booking-action-row">
          <button onClick={resend} className="px-4 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm inline-flex items-center justify-center gap-2" data-testid="resend-confirmation-btn">
            <RefreshCw className="w-4 h-4 shrink-0" /> <span className="truncate">Resend confirmation</span>
          </button>
          <button onClick={() => setReassignOpen(true)} className="px-4 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm inline-flex items-center justify-center gap-2" data-testid="reassign-vehicle-btn">
            <ArrowRightLeft className="w-4 h-4 shrink-0" /> <span className="truncate">Reassign vehicle</span>
          </button>
          {/* "Add" is gated on the business-level driver-services toggle: a
              self-drive-only business shouldn't discover this control. Once
              a booking is already with_driver, "Remove" stays available
              regardless, so turning the toggle off later never strands an
              existing driver-service booking. */}
          {((b.rental_service_type || "self_drive") === "with_driver" || company?.driver_services_enabled) && (
            <button onClick={async () => {
              const next = (b.rental_service_type || "self_drive") === "with_driver" ? "self_drive" : "with_driver";
              if (next === "self_drive" && !window.confirm("Switch to self-drive? Any assigned driver will be unassigned.")) return;
              try {
                await api.put(`/bookings/${id}/rental-service-type`, { rental_service_type: next });
                toast.success(next === "with_driver" ? "Booking now includes a driver" : "Booking set to self-drive");
                load();
              } catch (err) { toast.error(formatApiError(err)); }
            }} className="px-4 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm inline-flex items-center justify-center gap-2" data-testid="toggle-rental-service-type-btn">
              <Car className="w-4 h-4 shrink-0" />
              <span className="truncate">{(b.rental_service_type || "self_drive") === "with_driver" ? "Remove driver service" : "Add driver service"}</span>
            </button>
          )}
          {b.status === "ongoing" && !b.extension_pending_confirmation && (
            <button onClick={() => setExtendOpen(true)} className="px-4 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm inline-flex items-center justify-center gap-2" data-testid="extend-rental-btn">
              <Clock className="w-4 h-4 shrink-0" /> <span className="truncate">Extend Rental</span>
            </button>
          )}
          {(b.status === "completed" || inspections.return) && <button onClick={() => setInspectionOpen("return")} className="px-4 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm inline-flex items-center justify-center gap-2" data-testid="inspection-return-btn">
            <ClipboardCheck className="w-4 h-4 shrink-0" /> <span className="truncate">{inspections.return ? "Return inspection ✓" : "Return inspection"}</span>
          </button>}
          {b.status === "confirmed" && (
            <button onClick={() => setHandoverWizardOpen(true)} className="px-4 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm inline-flex items-center justify-center gap-2" data-testid="mark-picked-up-btn">
              <KeyRound className="w-4 h-4 shrink-0" /> <span className="truncate">Start Pickup Handover</span>
            </button>
          )}
          {!['completed', 'cancelled', 'no_show'].includes(b.status) && <button onClick={() => action("no-show", "Mark no-show")} className="px-4 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm inline-flex items-center justify-center gap-2" data-testid="no-show-btn">
            <Clock className="w-4 h-4 shrink-0" /> <span className="truncate">No-show</span>
          </button>}
          {!['completed', 'cancelled', 'no_show'].includes(b.status) && <button onClick={() => action("cancel", "Cancel booking")} className="px-4 py-2 rounded-lg border border-red-300 text-red-700 bg-white hover:bg-red-50 text-sm inline-flex items-center justify-center gap-2 col-span-2 sm:col-span-1" data-testid="cancel-btn">
            <Ban className="w-4 h-4 shrink-0" /> <span className="truncate">Cancel</span>
          </button>}
        </div>
      </div>

      {(b.status === "ongoing" || b.status === "completed") && (
        <ReturnReadinessPanel
          booking={b}
          returnInspection={inspections.return}
          nextBooking={data.next_booking}
          busy={returnBusy}
          onRecordReturn={recordReturn}
          onOpenInspection={() => setInspectionOpen("return")}
          onOpenNextBooking={data.next_booking ? () => nav(`/app/bookings/${data.next_booking.id}`) : undefined}
        />
      )}

      {b.safety_alert_status === "active" && (
        <div className="rounded-lg border-2 border-red-400 bg-red-50 p-4 flex items-start gap-3 flex-wrap"
             data-testid="active-rental-safety-alert-banner">
          <AlertOctagon className="w-5 h-5 text-red-700 mt-0.5 flex-shrink-0" />
          <div>
            <div className="font-display font-semibold text-red-900">ACTIVE RENTAL SAFETY ALERT</div>
            <p className="text-sm text-red-900/80 mt-0.5">
              A ConfirmPH Safety Hold was placed on this vehicle while it's still out with the renter.
              The rental stays Ongoing - contact the renter, arrange assistance, or assign a replacement vehicle.
            </p>
            {b.active_safety_hold_id && (
              <Link to={`/app/vehicles/${b.item_id}`} className="text-sm font-medium text-red-900 underline hover:no-underline mt-1 inline-block">
                View Safety Hold on this vehicle →
              </Link>
            )}
          </div>
        </div>
      )}

      {b.reconfirmation_required && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 flex items-start justify-between gap-3 flex-wrap"
             data-testid="reconfirmation-required-banner">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-700 mt-0.5" />
            <div>
              <div className="font-semibold text-amber-900">Booking details changed since the customer last confirmed</div>
              <p className="text-sm text-amber-900/80 mt-0.5">
                The customer hasn't reconfirmed the current terms yet. Resend the confirmation link when you're ready.
              </p>
            </div>
          </div>
          <button onClick={resend} className="px-3 py-1.5 rounded-lg bg-amber-600 text-white text-sm hover:opacity-90 whitespace-nowrap"
                  data-testid="reconfirmation-banner-resend-btn">
            Resend confirmation
          </button>
        </div>
      )}

      {b.extension_pending_confirmation && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 flex items-start gap-3 flex-wrap"
             data-testid="extension-pending-banner">
          <Clock className="w-5 h-5 text-amber-700 mt-0.5" />
          <div>
            <div className="font-semibold text-amber-900">Extension pending customer confirmation</div>
            <p className="text-sm text-amber-900/80 mt-0.5">
              Proposed new return: {fmtDate(b.pending_extension_end_datetime)}. The rental stays Ongoing until the customer confirms.
            </p>
          </div>
        </div>
      )}

      <ChangeRequestReviewPanel
        key={b.client_change_request?.requested_at || (b.change_requested ? "pending" : "none")}
        bookingId={id} booking={b} onChanged={load}
      />

      {data.booking.created_source && ["in_app_booking", "public_booking_request"].includes(data.booking.created_source) && (
        <InAppBookingActions booking={data.booking} onReload={load} />
      )}

      <ReviewInviteCard booking={b} />

      {data.booking.source_inquiry_id && (
        <div
          data-testid="booking-from-inquiry-banner"
          className="rounded-lg border border-[#1B4332]/20 bg-[#1B4332]/5 p-3 text-sm flex items-center gap-2"
        >
          <span className="text-[#1B4332] font-medium">Created from inquiry</span>
          <Link
            to={`/app/inquiries`}
            className="text-xs text-[#1B4332] underline"
            data-testid="booking-source-inquiry-link"
          >
            View inquiries →
          </Link>
        </div>
      )}

      {data.risk_reasons && data.risk_reasons.length > 0 && (
        <div
          data-testid="risk-reasons-banner"
          className={
            data.risk_reasons.some(r => r.level === "critical")
              ? "rounded-lg border border-red-200 bg-red-50 p-4"
              : "rounded-lg border border-amber-200 bg-amber-50 p-4"
          }
        >
          <div className="flex items-start gap-3">
            <AlertTriangle
              className={
                "w-5 h-5 mt-0.5 " +
                (data.risk_reasons.some(r => r.level === "critical") ? "text-red-700" : "text-amber-700")
              }
            />
            <div className="flex-1">
              <div className={
                "font-semibold " +
                (data.risk_reasons.some(r => r.level === "critical") ? "text-red-900" : "text-amber-900")
              } data-testid="risk-reasons-title">
                {data.risk_reasons.some(r => r.level === "critical")
                  ? "This booking needs attention"
                  : "Heads-up: review before pickup"}
              </div>
              <ul className="mt-2 space-y-1.5 text-sm" data-testid="risk-reasons-list">
                {data.risk_reasons.map((r, i) => (
                  <li key={i} className={r.level === "critical" ? "text-red-900" : "text-amber-900"}>
                    <span className="font-medium uppercase text-[10px] tracking-[0.15em] mr-2">
                      {r.level === "critical" ? "Critical" : r.level === "warning" ? "Warning" : "Info"}
                    </span>
                    {r.message}
                    {r.link && (
                      <Link to={r.link} className="ml-2 underline hover:no-underline text-xs">View →</Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {data.conflicts?.has_critical && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 flex items-start gap-3" data-testid="detail-conflict-critical">
          <AlertTriangle className="w-5 h-5 text-red-700 mt-0.5" />
          <div>
            <div className="font-semibold text-red-900">Double-booking detected</div>
            <ul className="text-sm text-red-900 mt-1 list-disc pl-5">
              {data.conflicts.overlaps.map((o) => (
                <li key={o.id}>{o.booking_reference} · {o.customer_name}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {data.incident && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 flex items-start gap-3" data-testid="detail-incident-banner">
          <AlertOctagon className="w-5 h-5 text-red-700 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-red-900">{data.incident.incident.title}</div>
            <div className="text-sm text-red-900 mt-0.5">
              Impact: <span className="font-medium">{(data.incident.impact_level || "").replace(/_/g, " ")}</span>
              {data.incident.notification_status && (
                <> · Customer notification: <span className="font-medium">{data.incident.notification_status.replace(/_/g, " ")}</span></>
              )}
            </div>
            <Link to={`/app/incidents/${data.incident.incident.id}`} className="text-sm font-medium text-red-900 underline hover:no-underline mt-1 inline-block" data-testid="detail-incident-link">
              View incident →
            </Link>
          </div>
        </div>
      )}

      {data.confirmation_link && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4" data-testid="confirmation-link-banner">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-emerald-900">
              <Mail className="w-4 h-4" />
              <span className="text-sm">Active confirmation link (mock email logged)</span>
            </div>
            <button onClick={copyLink} className="px-3 py-1.5 rounded-md bg-white border border-emerald-300 hover:bg-emerald-50 text-sm inline-flex items-center gap-2" data-testid="copy-link-btn">
              <Copy className="w-3.5 h-3.5" /> Copy link
            </button>
          </div>
          <div className="mt-2 text-xs font-mono break-all text-emerald-900">{data.confirmation_link}</div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-xl border border-slate-200 bg-white p-6 space-y-4">
          <h2 className="font-display text-lg font-semibold">Booking details</h2>
          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            <Info label="Vehicle" value={b.vehicle ? (
              <span className="inline-flex items-center gap-2 flex-wrap">
                {b.vehicle.name} {b.vehicle.plate_number ? `· ${b.vehicle.plate_number}` : ""}
                <StatusBadge status={b.vehicle.status} type="vehicle" data-testid="detail-vehicle-status-badge" />
              </span>
            ) : "-"} />
            <Info label="Customer email" value={b.customer_email} />
            <Info label="Customer phone" value={b.customer_phone || "-"} />
            <Info label="Pickup" value={b.pickup_location || "-"} />
            <Info label="Return" value={b.return_location || "-"} />
            <Info label="Start" value={fmtDate(b.start_datetime)} />
            <Info label="End" value={fmtDate(b.end_datetime)} />
            <Info label="Price" value={fmtCurrency(b.price)} mono />
            <Info label="Deposit" value={fmtCurrency(b.deposit_required)} mono />
            <Info label="Created" value={fmtRelative(b.created_at)} />
          </div>
          {b.customer_visible_notes && (
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Customer-visible notes</div>
              <p className="text-sm mt-1 whitespace-pre-line">{b.customer_visible_notes}</p>
            </div>
          )}
          {b.internal_notes && (
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Internal notes</div>
              <p className="text-sm mt-1 whitespace-pre-line bg-amber-50 border border-amber-200 rounded-md p-2">{b.internal_notes}</p>
            </div>
          )}
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-6">
          <div className="flex items-center justify-between gap-2 mb-3">
            <div className="flex items-center gap-2">
              <History className="w-4 h-4 text-slate-500" />
              <h2 className="font-display text-lg font-semibold">Timeline</h2>
            </div>
            <Link
              to={`/app/bookings/${id}/timeline`}
              data-testid="booking-timeline-open-link"
              className="text-xs font-medium text-[#1B4332] hover:underline"
            >
              Full timeline →
            </Link>
          </div>
          <ol className="space-y-3 max-h-[420px] overflow-y-auto pr-2" data-testid="audit-log">
            {data.audit_logs.map((l) => (
              <li key={l.id} className="text-sm border-l-2 border-slate-200 pl-3">
                <div className="font-medium text-slate-900">{l.description}</div>
                <div className="text-xs text-slate-500">{fmtDate(l.created_at)}</div>
              </li>
            ))}
            {data.audit_logs.length === 0 && (
              <li className="text-sm text-slate-500">No events yet.</li>
            )}
          </ol>
        </div>
      </div>

      {!financeLocked && (
        <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="booking-finance-records">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Wallet className="w-4 h-4 text-slate-500" />
              <h2 className="font-display text-lg font-semibold">Linked finance records</h2>
            </div>
            <Link to={`/app/administration?booking=${data.booking.id}`} className="text-sm text-[#1B4332] hover:underline">
              Open in Administration →
            </Link>
          </div>
          {financeRecords.length === 0 ? (
            <div className="mt-3 text-sm text-slate-500">No finance records linked to this booking yet.</div>
          ) : (
            <ul className="mt-3 divide-y divide-slate-100">
              {financeRecords.map((r) => (
                <li key={r.id} className="py-2 flex items-center justify-between gap-2 text-sm">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium truncate">{r.title}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border uppercase tracking-wider ${statusTone(r.status)}`}>{r.status}</span>
                      <span className="text-xs text-slate-500">{recordMeta(r.record_type).label}</span>
                    </div>
                    <div className="text-xs text-slate-500">{r.transaction_date}{r.payment_method ? ` · ${r.payment_method}` : ""}</div>
                  </div>
                  <span className="font-mono font-semibold">{fmtPHP(r.amount)}</span>
                </li>
              ))}
            </ul>
          )}


        {/* Location Section */}
        {(data.pickup_location_data || data.return_location_data || data.distance_from_base_km) && (
          <div className="bg-white rounded-lg border p-4">
            <h3 className="text-sm font-semibold uppercase tracking-widest text-slate-500 mb-3 flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              Location & Distance
            </h3>
            <div className="space-y-3">
              {data.pickup_location_data && (
                <div>
                  <div className="text-xs font-medium text-slate-600 mb-1">Pickup Location</div>
                  <div className="flex items-start gap-2">
                    <Navigation className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div className="text-sm">
                      {data.pickup_location_data.label && (
                        <div className="font-medium">{data.pickup_location_data.label}</div>
                      )}
                      {data.pickup_location_data.address && (
                        <div className="text-slate-600">{data.pickup_location_data.address}</div>
                      )}
                      {data.pickup_location_data.lat && data.pickup_location_data.lng && (
                        <div className="text-xs text-slate-500 mt-1">
                          📍 {data.pickup_location_data.lat.toFixed(6)}, {data.pickup_location_data.lng.toFixed(6)}
                        </div>
                      )}
                      {data.pickup_location_data.notes && (
                        <div className="text-xs text-slate-600 mt-1 italic">{data.pickup_location_data.notes}</div>
                      )}
                    </div>
                  </div>
                  {data.distance_from_base_km !== null && data.distance_from_base_km !== undefined && (
                    <div className="mt-2 text-xs text-slate-600 bg-slate-50 rounded px-2 py-1 inline-block">
                      Approx. {data.distance_from_base_km.toFixed(1)} km from business base
                    </div>
                  )}
                </div>
              )}
              
              {data.return_location_data && (
                <div>
                  <div className="text-xs font-medium text-slate-600 mb-1">Return Location</div>
                  <div className="flex items-start gap-2">
                    <Navigation className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <div className="text-sm">
                      {data.return_location_data.label && (
                        <div className="font-medium">{data.return_location_data.label}</div>
                      )}
                      {data.return_location_data.address && (
                        <div className="text-slate-600">{data.return_location_data.address}</div>
                      )}
                      {data.return_location_data.lat && data.return_location_data.lng && (
                        <div className="text-xs text-slate-500 mt-1">
                          📍 {data.return_location_data.lat.toFixed(6)}, {data.return_location_data.lng.toFixed(6)}
                        </div>
                      )}
                      {data.return_location_data.notes && (
                        <div className="text-xs text-slate-600 mt-1 italic">{data.return_location_data.notes}</div>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              <div className="flex gap-2 text-xs">
                {data.delivery_required && (
                  <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded-full">Delivery Required</span>
                )}
                {data.return_same_as_pickup && (
                  <span className="px-2 py-1 bg-green-50 text-green-700 rounded-full">Return to Pickup Location</span>
                )}
              </div>
            </div>
          </div>
        )}

        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="font-display text-lg font-semibold">Notifications</h2>
        <div className="mt-3 divide-y divide-slate-100">
          {data.notifications.length === 0 && <div className="text-sm text-slate-500 py-4">No notifications yet.</div>}
          {data.notifications.map((n) => (
            <div key={n.id} className="py-3 text-sm">
              <div className="flex flex-wrap items-center gap-2 justify-between">
                <span className="font-medium">{n.subject}</span>
                <span className="text-xs text-slate-500">{fmtDate(n.created_at)} · {n.status}</span>
              </div>
              <div className="text-xs text-slate-500">{n.channel} → {n.recipient}</div>
              {n.link && <div className="text-xs font-mono break-all text-slate-600 mt-1">{n.link}</div>}
            </div>
          ))}
        </div>
      </div>

      <PickupHandoverPanel bookingId={id} onChanged={() => { load(); loadInspections(); }} />

      <DriverAssignmentPanel bookingId={id} data={b} onChanged={load} />
      <RenterDriverPanel bookingId={id} data={b} onChanged={load} />
      <RentalLifecyclePanel bookingId={id} />

      <StaffAssignmentPanel bookingId={id} data={b} onChanged={load} />

      <ResolveDepositPanel bookingId={id} onChanged={load} highlight={justReturned} />

      <DepositPanel bookingId={id} onChanged={load} />

      <BookingFinancePanel bookingId={id} onChanged={load} />

      <CommunicationsPanel bookingId={id} />

      <BookingChatPanel bookingId={id} viewerType="staff" />

      <BookingRiskChip bookingId={id} />

      <BookingTimeline bookingId={id} />

      <ConfirmationHistoryPanel bookingId={id} />

      <DamageClarificationCard bookingId={id} onResolved={load} />

      <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="booking-reminders-card">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg font-semibold">Reminders</h2>
          <Link to="/app/reminders" className="text-sm text-[#1B4332] hover:underline" data-testid="booking-reminders-all-link">View all →</Link>
        </div>
        <div className="mt-3 divide-y divide-slate-100">
          {reminders.length === 0 && <div className="text-sm text-slate-500 py-4">No reminders linked to this booking yet.</div>}
          {reminders.map((r) => (
            <div key={r.id} className="py-3 text-sm" data-testid={`booking-reminder-${r.id}`}>
              <div className="flex flex-wrap items-center gap-2 justify-between">
                <span className="font-medium">{r.title}</span>
                <span className="text-xs text-slate-500">{r.priority} · {r.status} · due {fmtDate(r.due_at)}</span>
              </div>
              <div className="text-xs text-slate-500 line-clamp-2">{r.message}</div>
            </div>
          ))}
        </div>
      </div>

      <ReassignVehicleModal
        bookingId={id}
        open={reassignOpen}
        onClose={() => setReassignOpen(false)}
        onDone={load}
      />
      <ExtendRentalModal
        bookingId={id}
        booking={b}
        open={extendOpen}
        onClose={() => setExtendOpen(false)}
        onDone={load}
      />
      <InspectionSheet
        bookingId={id}
        type={inspectionOpen}
        open={!!inspectionOpen}
        onClose={() => setInspectionOpen(null)}
        onDone={() => {
          loadInspections(); load();
          if (inspectionOpen === "return") {
            setJustReturned(true);
            setTimeout(() => {
              document.querySelector('[data-testid="resolve-deposit-panel"]')
                ?.scrollIntoView({ behavior: "smooth", block: "center" });
            }, 300);
            setTimeout(() => setJustReturned(false), 6000);
          }
        }}
      />
      <PickupHandoverWizard
        bookingId={id}
        open={handoverWizardOpen}
        onClose={() => setHandoverWizardOpen(false)}
        onDone={() => { loadInspections(); load(); }}
      />
    </div>
  );
}

function Info({ label, value, mono }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">{label}</div>
      <div className={`mt-0.5 ${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  );
}


function InAppBookingActions({ booking, onReload }) {
  const isAwaitingStaffApproval = booking.status === "awaiting_staff_approval";
  const isHold = isAwaitingStaffApproval && booking.is_hold;
  const isChangeRequested = isAwaitingStaffApproval && booking.change_requested;
  const isPendingReview = isAwaitingStaffApproval && !booking.is_hold && !booking.change_requested;
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState("");
  const [showReject, setShowReject] = useState(false);

  const call = async (path, body) => {
    setBusy(true);
    try {
      await api.post(`/in-app-bookings/bookings/${booking.id}${path}`, body || undefined);
      toast.success("Done");
      onReload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const tone = isHold ? "amber" : "emerald";
  const bg = tone === "amber" ? "bg-amber-50 border-amber-200" : "bg-emerald-50 border-emerald-200";

  return (
    <div className={`rounded-xl border ${bg} p-5`} data-testid="in-app-booking-actions">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-700">
            {booking.created_source === "in_app_booking" ? "Automatic hold" : "Customer request"}
          </div>
          <h3 className="font-display text-lg font-semibold mt-0.5">
            {isHold ? "Temporary hold awaiting your decision"
             : isChangeRequested ? "Customer requested a change: review and respond"
             : isPendingReview ? "New booking request from customer"
             : `Booking status: ${booking.status}`}
          </h3>
          {booking.hold_expires_at && (
            <p className="text-xs text-amber-900 mt-1">
              Hold expires {new Date(booking.hold_expires_at).toLocaleString()}
            </p>
          )}
          {booking.accepted_terms_at && (
            <p className="text-xs text-slate-600 mt-1">
              Customer accepted your terms on {new Date(booking.accepted_terms_at).toLocaleString()}
              {booking.accepted_terms_version && ` (v ${booking.accepted_terms_version})`}.
            </p>
          )}
        </div>
        {isAwaitingStaffApproval && (
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => call("/approve")} disabled={busy}
                    data-testid="in-app-approve-btn"
                    className="px-3 py-2 rounded-lg btn-brand text-sm inline-flex items-center gap-1">
              <CheckCircle className="w-4 h-4" /> Approve &amp; confirm
            </button>
            <button onClick={() => setShowReject((s) => !s)} disabled={busy}
                    data-testid="in-app-reject-toggle"
                    className="px-3 py-2 rounded-lg border border-rose-300 text-rose-700 bg-white hover:bg-rose-50 text-sm inline-flex items-center gap-1">
              <Ban className="w-4 h-4" /> Reject
            </button>
            {isHold && (
              <button onClick={() => call("/release-hold")} disabled={busy}
                      data-testid="in-app-release-hold-btn"
                      className="px-3 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm">
                Release hold
              </button>
            )}
          </div>
        )}
      </div>
      {showReject && (
        <div className="mt-3 grid gap-2">
          <textarea value={reason} onChange={(e) => setReason(e.target.value)}
                    placeholder="(Optional) Reason: sent to the customer."
                    rows={2}
                    data-testid="in-app-reject-reason"
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm" />
          <button onClick={() => call("/reject", { reason })} disabled={busy}
                  data-testid="in-app-confirm-reject"
                  className="self-end px-3 py-2 rounded-lg bg-rose-600 text-white hover:bg-rose-700 text-sm">
            Confirm rejection
          </button>
        </div>
      )}
    </div>
  );
}
